import { z } from "zod";
import {
  inlineAddressSchema,
  type BaseAddressData
} from "@/components/addresses/address-schema";

export { inlineAddressSchema };
export type InlineAddressData = BaseAddressData;

/**
 * Validation schema for checkout address step
 *
 * Supports two modes for addresses:
 * 1. Saved address: Uses shippingAddressId/billingAddressId to reference saved customer address
 * 2. One-time address: Uses shippingAddress/billingAddress with inline address data
 *
 * Validates:
 * - Contact information (email, phone)
 * - Shipping address (either ID or inline data required)
 * - Billing address (required unless "same as shipping" is checked)
 */
export const checkoutAddressSchema = z
  .object({
    // Contact info
    email: z
      .string()
      .min(1, "Email is required")
      .email("Please enter a valid email address"),

    phone: z
      .string()
      .min(1, "Phone number is required")
      .regex(/^\+?[0-9\s\-()]+$/, "Please enter a valid phone number"),

    // Shipping address - either saved ID or inline data
    shippingAddressId: z.string().optional(),
    shippingAddress: inlineAddressSchema.optional(),

    // Billing address
    sameAsShipping: z.boolean(),
    billingAddressId: z.string().optional(),
    billingAddress: inlineAddressSchema.optional()
  })
  .refine(
    (data) => {
      // Must have either a saved address ID or inline address data for shipping
      const hasShippingId =
        data.shippingAddressId && data.shippingAddressId.length > 0;
      const hasShippingData = data.shippingAddress !== undefined;
      return hasShippingId || hasShippingData;
    },
    {
      message: "Please select or enter a shipping address",
      path: ["shippingAddressId"]
    }
  )
  .refine(
    (data) => {
      // If not same as shipping, must have either billing ID or inline billing data
      if (data.sameAsShipping) return true;
      const hasBillingId =
        data.billingAddressId && data.billingAddressId.length > 0;
      const hasBillingData = data.billingAddress !== undefined;
      return hasBillingId || hasBillingData;
    },
    {
      message: "Please select or enter a billing address",
      path: ["billingAddressId"]
    }
  );

export type CheckoutAddressFormData = z.infer<typeof checkoutAddressSchema>;

/**
 * Default empty values for checkout address form
 */
export const emptyCheckoutAddressFormValues: CheckoutAddressFormData = {
  email: "",
  phone: "",
  shippingAddressId: "",
  shippingAddress: undefined,
  sameAsShipping: true,
  billingAddressId: "",
  billingAddress: undefined
};
